import React, { useState, useEffect, useCallback} from 'react';
import { useSelector, useDispatch } from "react-redux";
import axios from 'axios';
import { updateUserStart, updateUserSuccess } from '../../redux/user/userSlice';
import { debounce } from 'lodash';

const InvoiceModal = React.memo(({ amount, onDone, onClose }) => {
  const [cutPercentage, setCutPercentage] = useState(0);
  const [modalAmount, setModalAmount] = useState(amount);
  const [calculatedInvoiceAmount, setCalculatedInvoiceAmount] = useState(0);

  const handleGenerate = useCallback(() => {
    const invoiceAmount = parseFloat(modalAmount);
    const percentage = parseFloat(cutPercentage);
    
    if (isNaN(invoiceAmount)) {
      alert('Please enter a valid amount');
      return;
    }

    if (isNaN(percentage)) {
      alert('Please enter a valid percentage');
      return;
    }

    if (percentage <= 0 || percentage >= 100) {
      alert('Percentage must be between 0 and 100');
      return;
    }

    const calculatedInvoice = (invoiceAmount / (percentage / 100)).toFixed(2);
    setCalculatedInvoiceAmount(calculatedInvoice);
  }, [modalAmount, cutPercentage]);

  const handleDone = useCallback(() => {
    if (calculatedInvoiceAmount > 0) {
      onDone(calculatedInvoiceAmount);
    }
  }, [onDone, calculatedInvoiceAmount]);

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-50 tw-flex tw-items-center tw-justify-center tw-overflow-y-auto tw-py-10">
      <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-lg tw-max-w-md tw-w-full tw-mx-4 tw-my-auto">
        <h2 className="tw-text-xl tw-font-bold tw-mb-4">Generate Invoice</h2>
        <div className="tw-mb-4">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Cut Percentage (%):
          </label>
          <input
            type="number"
            value={cutPercentage}
            onChange={(e) => setCutPercentage(e.target.value)}
            placeholder="Enter cut percentage"
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md"
            min="1"
            max="99"
          />
        </div>
        <div className="tw-mb-4">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Cash Amount:
          </label>
          <input
            type="number"
            value={modalAmount}
            onChange={(e) => setModalAmount(e.target.value)}
            placeholder="Enter amount"
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md"
            min="0"
            step="0.01"
          />
        </div>
        <div className="tw-flex tw-gap-2 tw-flex-wrap">
          <button 
            onClick={handleGenerate}
            className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700"
          >
            Generate
          </button>
          <button 
            onClick={handleDone}
            disabled={!calculatedInvoiceAmount}
            className="tw-px-4 tw-py-2 tw-bg-green-600 tw-text-white tw-rounded-md hover:tw-bg-green-700 disabled:tw-opacity-50"
          >
            Done
          </button>
          <button 
            onClick={onClose}
            className="tw-px-4 tw-py-2 tw-bg-gray-600 tw-text-white tw-rounded-md hover:tw-bg-gray-700"
          >
            Close
          </button>
        </div>
        {calculatedInvoiceAmount > 0 && (
          <p className="tw-mt-4 tw-text-lg">
            Calculated Invoice Amount: <span className="tw-font-bold">${calculatedInvoiceAmount}</span>
          </p>
        )}
      </div>
    </div>
  );
});

const TransferCredit = () => {
  const [loading, setLoading] = useState(false);
  const { currentUser } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [credit, setCredit] = useState({
    amount: '',
    receiver: '',
  });

  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchBalance = useCallback(async () => {
    try {
      if (!currentUser?._id) return;
      const res = await axios.get(`/api/credit/${currentUser._id}/balance`);
      setBalance(res.data.balance || 0);
    } catch (err) {
      console.error('Error fetching balance:', err);
      setBalance(0);
    }
  }, [currentUser?._id]);

  const fetchTransactions = useCallback(async () => {
    try {
      if (!currentUser?.phone) return;
      const res = await axios.get(`/api/credit/getCredit/${currentUser.phone}`, {
        params: {
          page: currentPage,
          limit: itemsPerPage
        }
      });
      
      const responseData = res.data;
      const credits = Array.isArray(responseData.credits) ? responseData.credits : [];
      
      setTransactions(credits);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
    }
  }, [currentUser?.phone, currentPage]);

  useEffect(() => {
    if (currentUser) {
      fetchBalance();
      fetchTransactions();
    }
  }, [currentUser, fetchBalance, fetchTransactions, currentPage]);

  const { amount, receiver } = credit;

  const onChange = useCallback((e) => {
    setCredit(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleTransfer = useCallback(async () => {
    if (!currentUser) {
      setError('User not authenticated');
      return;
    }

    const senderPhone = String(currentUser.phone);
    
    if (senderPhone === receiver.trim()) {
      setError('You cannot send credit to yourself');
      return;
    }

    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount)) {
      setError('Please enter a valid amount');
      return;
    }

    if (transferAmount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    if (transferAmount > currentUser.balance) {
      setError('Insufficient balance');
      return;
    }

    dispatch(updateUserStart());
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post("/api/credit/transfer", {
        amount: transferAmount,
        receiver: receiver.trim(),
        sender: senderPhone,
      });

      if (response.data.success) {
        // Get the updated sender balance from the response
        const newBalance = response.data.balances?.sender || (currentUser.balance - transferAmount);

        dispatch(updateUserSuccess({ 
          ...currentUser, 
          balance: newBalance 
        }));
        
        setCredit({ amount: '', receiver: '' });
        setSuccess('Credit transferred successfully!');
        setCurrentPage(1);
        
        // Refresh data after successful transfer
        setTimeout(async () => {
          await fetchBalance();
          await fetchTransactions();
        }, 100);
      } else {
        setError(response.data.msg || response.data.message || 'Error transferring credit');
      }
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err.response?.data?.message || err.response?.data?.msg || err.message || 'Error transferring credit');
    } finally {
      setLoading(false);
    }
  }, [amount, receiver, currentUser, dispatch, fetchBalance, fetchTransactions]);

  const debouncedHandleTransfer = useCallback(debounce(handleTransfer, 300), [handleTransfer]);

  const onSubmit = useCallback((e) => {
    e.preventDefault();
    debouncedHandleTransfer();
  }, [debouncedHandleTransfer]);

  useEffect(() => {
    return () => {
      debouncedHandleTransfer.cancel();
    };
  }, [debouncedHandleTransfer]);

  const handleDoneInModal = useCallback((calculatedAmount) => {
    if (calculatedAmount && !isNaN(calculatedAmount)) {
      setCredit(prev => ({ ...prev, amount: calculatedAmount }));
    }
    setShowModal(false);
  }, []);

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  return (
    <div className="tw-px-4 tw-py-2 tw-mt-10 tw-max-w-4xl tw-mx-auto tw-relative">
      <div className='tw-text-blue-800 tw-px-6 tw-py-4 tw-rounded-md tw-font-semibold tw-mt-6 tw-mb-6 tw-bg-white tw-shadow-lg'>
        <h1 className='tw-text-blue-800 tw-text-2xl'>Current Balance</h1>
        <h1 className='tw-text-green-800 tw-text-3xl tw-font-bold'>
          ${balance.toLocaleString()}
        </h1>
      </div>
      
      <div className='tw-rounded-md tw-bg-white tw-p-6 tw-shadow-lg tw-mb-8'>
        <h1 className='tw-font-semibold tw-mb-5 tw-text-2xl tw-text-blue-800'>
          Transfer <span className="tw-text-blue-600">Credit</span>
        </h1>
        
        {error && (
          <div className="tw-mb-4 tw-p-3 tw-bg-red-100 tw-text-red-700 tw-rounded-md">
            {error}
          </div>
        )}

        {success && (
          <div className="tw-mb-4 tw-p-3 tw-bg-green-100 tw-text-green-700 tw-rounded-md">
            {success}
          </div>
        )}

        <form onSubmit={onSubmit} className='tw-flex tw-flex-col tw-gap-4'>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700">Amount</label>
            <input
              type="number"
              name="amount"
              placeholder='Amount in Credit'
              value={amount}
              onChange={onChange}
              className='tw-rounded-md tw-border-2 tw-p-3 tw-border-gray-300 focus:tw-border-blue-800 tw-text-md'
              required
              step="0.01"
              min="0.01"
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700">Receiver Phone</label>
            <input
              type="tel"
              name="receiver"
              placeholder='Receiver Phone Number'
              className='tw-rounded-md tw-border-2 tw-p-3 tw-border-gray-300 focus:tw-border-blue-800 tw-text-md'
              value={receiver}
              onChange={onChange}
              required
            />
          </div>
          <div className="tw-flex tw-gap-3 tw-mt-2 tw-flex-wrap">
            <button
              type="submit"
              className="tw-text-white tw-bg-blue-800 tw-py-2 tw-px-6 tw-rounded-md hover:tw-bg-blue-900 disabled:tw-opacity-50"
              disabled={loading}
            >
              {loading ? "Processing..." : "Transfer"}
            </button>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="tw-text-blue-800 tw-bg-white tw-border tw-border-blue-800 tw-py-2 tw-px-6 tw-rounded-md hover:tw-bg-blue-50"
            >
              Generate Invoice
            </button>
          </div>
        </form>
      </div>
      
      {showModal && (
        <InvoiceModal
          amount={amount}
          onDone={handleDoneInModal}
          onClose={() => setShowModal(false)}
        />
      )}
      
      <div className='tw-mt-8 tw-bg-white tw-rounded-md tw-shadow-lg tw-overflow-hidden'>
        <h2 className='tw-text-xl tw-font-semibold tw-p-4 tw-border-b tw-border-gray-200'>
          Transaction History
        </h2>
        
        <div className="tw-overflow-x-auto">
          <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
            <thead className="tw-bg-blue-800">
              <tr>
                <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">Sender</th>
                <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">Receiver</th>
                <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">Amount</th>
                <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
              {transactions.length > 0 ? (
                transactions.map((transaction) => (
                  <tr key={transaction._id} className="hover:tw-bg-gray-50">
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">{transaction.sender}</td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">{transaction.receiver}</td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">${transaction.amount}</td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                      {new Date(transaction.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="tw-px-6 tw-py-4 tw-text-center tw-text-gray-500">
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-border-t tw-border-gray-200">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-bg-white tw-text-blue-800 hover:tw-bg-gray-50 disabled:tw-opacity-50"
          >
            Previous
          </button>
          <span className="tw-text-sm tw-text-gray-700">
            Page {currentPage}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={transactions.length < itemsPerPage}
            className="tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-bg-white tw-text-blue-800 hover:tw-bg-gray-50 disabled:tw-opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TransferCredit);